import { useEffect, useState } from "react";
import { appBuildId } from "../appVersion";

export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const UPDATE_CHECK_COOLDOWN_MS = 15 * 1000;
export const UPDATE_CHECK_TIMEOUT_MS = 10 * 1000;

const isReleaseMetadata = (value) =>
  value?.schemaVersion === 1 &&
  typeof value.buildId === "string" &&
  /^[a-zA-Z0-9._-]{1,128}$/.test(value.buildId);

export default function useDeploymentUpdate(enabled) {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    setUpdateAvailable(false);
    // Local dev servers do not publish release metadata.
    if (!enabled || !appBuildId) return undefined;

    let disposed = false;
    let activeController;
    let requestTimeout;
    let lastCheck = -Infinity;

    const check = async () => {
      if (
        disposed || activeController || document.visibilityState === "hidden" ||
        navigator.onLine === false ||
        Date.now() - lastCheck < UPDATE_CHECK_COOLDOWN_MS
      ) return;

      lastCheck = Date.now();
      const controller = new AbortController();
      activeController = controller;
      requestTimeout = setTimeout(() => controller.abort(), UPDATE_CHECK_TIMEOUT_MS);
      try {
        // Same-origin static metadata deliberately bypasses the authenticated API.
        const response = await fetch("/version.json", {
          cache: "no-store",
          credentials: "omit",
          redirect: "error",
          signal: controller.signal,
        });
        const contentType = response.headers.get("content-type")?.split(";")[0].trim().toLowerCase();
        if (!response.ok || response.redirected || contentType !== "application/json") return;
        const metadata = await response.json();
        if (!disposed && !controller.signal.aborted && isReleaseMetadata(metadata)) {
          setUpdateAvailable(metadata.buildId !== appBuildId);
        }
      } catch (_) {
        // Offline, failed deploys and SPA fallbacks must not interrupt Manager.
      } finally {
        clearTimeout(requestTimeout);
        activeController = undefined;
      }
    };

    check();
    const interval = setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      disposed = true;
      clearInterval(interval);
      clearTimeout(requestTimeout);
      activeController?.abort();
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [enabled]);

  return enabled && updateAvailable;
}
